<template>
  <div ref="racine" class="relative" data-tour="cloche">
    <button
      type="button"
      class="relative flex h-9 w-9 items-center justify-center rounded-full text-space-indigo transition-colors hover:bg-alice-blue dark:text-altoneo-50 dark:hover:bg-alt-dark-raised"
      :aria-label="aLire.length ? `${aLire.length} demande(s) avec un message à lire` : 'Aucun message à lire'"
      :aria-expanded="ouvert"
      aria-haspopup="true"
      @click="ouvert = !ouvert"
    >
      <AppIcon nom="cloche" :taille="19" />
      <span
        v-if="aLire.length"
        class="absolute -right-0.5 -top-0.5 flex h-4 min-w-4 items-center justify-center rounded-full bg-red-600 px-1 text-[10px] font-bold leading-none text-white"
        aria-hidden="true"
      >
        {{ aLire.length > 99 ? '99+' : aLire.length }}
      </span>
    </button>

    <div
      v-if="ouvert"
      class="absolute right-0 z-40 mt-2 w-80 overflow-hidden rounded-xl border border-slate-200 bg-white shadow-lg dark:border-alt-dark-border dark:bg-alt-dark-surface"
      role="dialog"
      aria-label="Messages à lire"
    >
      <p class="border-b border-slate-100 px-4 py-2 text-xs font-semibold uppercase tracking-wider text-slate-600 dark:border-alt-dark-border dark:text-alt-dark-muted">
        Messages à lire
      </p>
      <p v-if="!aLire.length" class="px-4 py-4 text-sm text-slate-600 dark:text-alt-dark-muted">
        Aucun message ne vous attend sur vos demandes.
      </p>
      <ul v-else class="max-h-80 overflow-y-auto">
        <li v-for="d in aLire" :key="d.id">
          <RouterLink
            :to="`/demandes/${d.id}`"
            class="block border-b border-slate-100 px-4 py-2 last:border-0 pa-survol dark:border-alt-dark-border"
            @click="ouvert = false"
          >
            <span class="block truncate text-sm font-medium text-space-indigo dark:text-altoneo-50">{{ d.objet }}</span>
            <span class="block truncate text-xs text-slate-600 dark:text-alt-dark-muted">
              {{ d.numero }} · {{ d.auteur ?? 'Message' }} · {{ depuis(d.dernierMessage) }}
            </span>
          </RouterLink>
        </li>
      </ul>
    </div>
  </div>
</template>

<script setup lang="ts">
import { onBeforeUnmount, onMounted, ref } from 'vue';
import { useToast } from 'altoneo-charting';
import type { DemandeALireDTO } from '@its/shared';
import { api } from '@/lib/api';
import { depuis } from '@/lib/affichage';
import { useTempsReel } from '@/composables/useTempsReel';
import { surRafraichissementCloche } from '@/lib/cloche';
import AppIcon from '@/components/AppIcon.vue';

/**
 * LA CLOCHE — les demandes où un message attend l'utilisateur (0096) : son
 * preneur en charge, et désormais son demandeur (DEC-038).
 * Elle se met à jour à son affichage, après chaque ouverture ou action sur une
 * fiche (`lib/cloche` : c'est l'ouverture qui marque lue) et sur le temps réel ;
 * un message NOUVEAU depuis le dernier passage s'annonce aussi par un toast.
 */
const aLire = ref<DemandeALireDTO[]>([]);
const ouvert = ref(false);
const racine = ref<HTMLElement | null>(null);
const toast = useToast();
let premier = true;

async function charger(): Promise<void> {
  try {
    const nouvelles = await api.get<DemandeALireDTO[]>('/demandes/a-lire');
    if (!premier) {
      const avant = new Map(aLire.value.map((d) => [d.id, d.dernierMessage]));
      for (const d of nouvelles) {
        if (avant.get(d.id) !== d.dernierMessage) toast.info(`Nouveau message sur ${d.numero} : ${d.objet}`);
      }
    }
    aLire.value = nouvelles;
    premier = false;
  } catch {
    /* la cloche n'est pas critique : elle se remettra au prochain passage */
  }
}

// Les rafales (une intervention produit plusieurs événements) se regroupent.
let enAttente: ReturnType<typeof setTimeout> | undefined;
useTempsReel(() => {
  clearTimeout(enAttente);
  enAttente = setTimeout(() => void charger(), 600);
});

const fermerDehors = (e: MouseEvent) => {
  if (ouvert.value && racine.value && !racine.value.contains(e.target as Node)) ouvert.value = false;
};
const fermerEchap = (e: KeyboardEvent) => {
  if (e.key === 'Escape') ouvert.value = false;
};
let desabonner: (() => void) | undefined;
onMounted(() => {
  void charger();
  desabonner = surRafraichissementCloche(() => void charger());
  document.addEventListener('click', fermerDehors);
  document.addEventListener('keydown', fermerEchap);
});
onBeforeUnmount(() => {
  desabonner?.();
  document.removeEventListener('click', fermerDehors);
  document.removeEventListener('keydown', fermerEchap);
});
</script>
